
export interface Camera {
    center: Coordinates;
    zoom: number;
}

export interface Coordinates {
    lat: number, lng: number
}

export const CHICAGO = { lat: 41.87832960158098 - 0.007, lng: -87.6298 - 0.025 } 

function getCenter(points) {
    let maxLat = Math.max(...points.map(p => p.lat));
    let minLat = Math.min(...points.map(p => p.lat));
    let maxLng = Math.max(...points.map(p => p.lng));
    let minLng = Math.min(...points.map(p => p.lng));

    const center = {
        lat: (maxLat + minLat) / 2,
        lng: (maxLng + minLng) / 2,
    };
    return center;
}

function getProximityZoom(proximity) {
    return 14.8 - (proximity * .4)
}

const focusedZoomLevel = 14;
const zipZoomLevel = 13.5;
const networkZoomLevel = 11.5;
const pinZoomLevel = 14;

export const MAP_RESET = 'reset';
export const MAP_FOCUS_SCHOOL = 'focus-school';
export const MAP_FOCUS_ZIP = 'focus-zip';
export const MAP_FOCUS_NEIGHBORHOOD = 'focus-neighborhood';
export const MAP_FOCUS_POINT = 'focus-point';
export const MAP_FOCUS_ADDRESS = 'focus-address';
export const MAP_SET_PROXIMITY = 'set-proximity';
export const MAP_FOCUS_POLYGON = 'focus-polygon';
export const MAP_SET_PIN_LOCATION = 'set-pin';
export const MAP_CLEAR_PIN = 'clear-pin';
export const MAP_FOCUS_POINT_CLOSE = 'focus-point-close';
export const MAP_FOCUS_NETWORK_NO_POLYGONE = 'network-without-polygone';

export const mapControllerDefaultState = {
    camera: {
        center: CHICAGO,
        zoom: 12.5,
    },
    
    proximity: 2,
    pin: null,
    overlay: null
}
/**
 * Calculate the appropriate zoom level based on the geographical bounds (min/max latitudes and longitudes)
 * and the map's pixel dimensions. This approach is useful for dynamically setting the zoom level when displaying
 * multiple markers on the map.
 *
 * @param {Array} markers - An array of objects representing the markers. Each marker object should have 'lat' and 'lng' properties.
 * @param {Object} mapDim - An object representing the map's dimensions with 'width' and 'height' properties in pixels.
 * @param {number} [maxZoom=15] - The maximum zoom level allowed (closer view). Default is 15.
 * @param {number} [minZoom=11] - The minimum zoom level allowed (farther view). Default is 11.
 * @returns {number} - The calculated zoom level that ensures all markers are visible within the map's bounds.
 */
export function getNoBoundaryZoomByMarkersArray(markers, mapDim, maxZoom = 15, minZoom = 11): Camera {
    if (markers.length === 0) {
        return  {
            zoom: minZoom,
            center: CHICAGO
        }
    }
    const coordinatesList = markers.map(marker => {
        return {
            lat: marker[1],
            lng: marker[0]
        }
    });
    // Step 1: Find the min and max latitudes and longitudes
    let minLat = coordinatesList[0].lat;
    let maxLat = coordinatesList[0].lat;
    let minLng = coordinatesList[0].lng;
    let maxLng = coordinatesList[0].lng;

    coordinatesList.forEach((marker) => {
        if (marker.lat < minLat) minLat = marker.lat;
        if (marker.lat > maxLat) maxLat = marker.lat;
        if (marker.lng < minLng) minLng = marker.lng;
        if (marker.lng > maxLng) maxLng = marker.lng;
    });

    // Step 2: Calculate the differences (distances)
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const ZOOM_MAX = maxZoom;
    const ZOOM_MIN = minZoom;

    // Step 3: Calculate the zoom level for both latitude and longitude differences
    const latZoom = Math.log2((mapDim.height) / latDiff);
    const lngZoom = Math.log2((mapDim.width) / lngDiff);


    // Use the minimum zoom level so that the map fits within both dimensions
    const zoom = Math.min(latZoom, lngZoom);
    
    //Calculate the center of the bounding box 
    const centerLat = ((minLat + maxLat) / 2) - (latDiff * 0.1); //0.1 is random number matched to the visual, feel free to play with it more
    const centerLng = (minLng + maxLng) / 2;
    const centerNew = calculateCenterOfMarkers(markers);
    const center = { lat: centerLat, lng: centerLng };

    const zoomData = Math.max(Math.min(zoom, ZOOM_MAX), ZOOM_MIN);
    return {
        zoom: Math.floor(zoomData),
        center: centerNew
    }
}

 export function calculateCenterOfMarkers(markers): Coordinates {
    if (markers.length === 0) {
        return null;
    }

    let bounds = new google.maps.LatLngBounds();
    const coordinatesList = markers.map(marker => {
        return {
            lat: marker[1],
            lng: marker[0]
        }
    });

    coordinatesList.forEach(marker => {
        bounds.extend(marker);
    });

     let center = bounds.getCenter();
    return {
        lat: center.lat(),
        lng: center.lng()
    };
}

export const mapControllerReducer = (state, payload) => {
    switch (payload.action) {
        case MAP_RESET:
            return {
                ...mapControllerDefaultState, camera: { ...mapControllerDefaultState.camera }
            };
        case MAP_SET_PROXIMITY:
            return {
                ...state, proximity: payload.proximity, camera: {
                    center: {
                        ...state.camera.center
                    },
                    zoom: getProximityZoom(payload.proximity)
                }
            };
        case MAP_FOCUS_POINT:
            return {
                ...state,
                camera: {
                    center: payload.point,
                    zoom: getProximityZoom(state.proximity)
                }
            };
        case MAP_FOCUS_POINT_CLOSE:
            return {
                ...state,
                camera: {
                    center: payload.point,
                    zoom: 14
                }
            };
        case MAP_FOCUS_POLYGON:
            const getPolygoneCenter = (type: string) => {
                if (type === "MultiPolygon") {
                    return getCenter(payload.polygon.geometry.coordinates[0][0].map(arr => ({ lat: arr[1], lng: arr[0] })))
                } else if (type === "GeometryCollection") {
                    const geometry = payload.polygon.geometry;
                    if (geometry.geometries && geometry.geometries.some(i => i.type === "Polygon")) {
                        return getCenter(
                            geometry.geometries.find(i => i.type === "Polygon").coordinates[0].map(arr => ({ lat: arr[1], lng: arr[0] }))
                        )
                    }
                }
                else {
                    return getCenter(payload.polygon.geometry.coordinates[0].map(arr => ({ lat: arr[1], lng: arr[0] })))
                }
               
            }
            return {
                ...state,
                camera: payload.polygon && payload.polygon.geometry
                    ? {
                        center: getPolygoneCenter(payload.polygon.geometry.type),
                        zoom: payload.overlay && payload.overlay.name === 'NETWORK' ? networkZoomLevel : zipZoomLevel
                    }
                    : 11
            }
        case MAP_FOCUS_NETWORK_NO_POLYGONE:
            return {
                ...state,
                camera: {
                    center: payload.point ? payload.point : CHICAGO,
                    zoom: payload.zoom ? payload.zoom : 11,
                },
                
            }
        case MAP_SET_PIN_LOCATION:
            return {
                ...state,
                camera: {
                    center: payload.point,
                    zoom: getProximityZoom(state.proximity),
                },
                pin: payload.point
            }
        case MAP_CLEAR_PIN:
            return {
                ...state,
                camera: {
                    ...mapControllerDefaultState.camera
                },
                pin: null
            }
        default:
            return state;
    }
}